import { Controller } from "@hotwired/stimulus"

// Generic inline rule picker (used for CPT/HCPCS/DRG/ICD-10/Taxonomies):
// - Autocomplete selection adds a pill (with its own hidden input)
// - Removing the pill removes the input (remove-element controller)
// - De-dupe based on existing hidden inputs
//
// Data attributes (all optional):
// - data-preferred-provider-rule-picker-label-prefix-value="CPT" (used to format label as "CPT 99282")
// - data-preferred-provider-rule-picker-use-selected-name-value="true" (use selectedNameField text for label, e.g. taxonomies)
// - data-preferred-provider-rule-picker-uppercase-value="true" (uppercases the value stored)
export default class extends Controller {
  static values = {
    labelPrefix: String,
    useSelectedName: Boolean,
    uppercase: Boolean
  }

  static targets = [
    "selectedIdField",
    "selectedNameField",
    "autocompleteInput",
    "pillTemplate",
    "pills"
  ]

  addFromAutocomplete() {
    let value = (this.selectedIdFieldTarget.value || "").trim()
    if (!value) return

    if (this.uppercaseValue) value = value.toUpperCase()

    // De-dupe
    if (this.pillValues().includes(value)) {
      this.clearAutocompleteSelection()
      return
    }

    const pillId = `preferred-provider-rule-${Date.now()}-${Math.random().toString(16).slice(2)}`

    const templateRoot = this.pillTemplateTarget?.content?.firstElementChild
    if (!templateRoot) return

    const wrapper = templateRoot.cloneNode(true)
    wrapper.id = pillId
    wrapper.dataset.ruleValue = value

    const hiddenInput = wrapper.querySelector('input[type="hidden"]')
    if (hiddenInput) hiddenInput.value = value

    const labelEl = wrapper.querySelector("[data-chip-label]")
    const label = this.formatLabel(value, (this.selectedNameFieldTarget.value || "").trim())
    if (labelEl) labelEl.textContent = label

    const removeButton = wrapper.querySelector('[data-controller="remove-element"]')
    if (removeButton) {
      removeButton.dataset.elementId = pillId
      removeButton.setAttribute("aria-label", `Remove ${label}`)
    }

    this.pillsTarget.appendChild(wrapper)
    this.clearAutocompleteSelection(true)
  }

  pillValues() {
    const templateRoot = this.pillTemplateTarget?.content?.firstElementChild
    const templateHiddenInput = templateRoot?.querySelector('input[type="hidden"]')
    const inputName = templateHiddenInput?.getAttribute("name")
    if (!inputName) return []

    return Array.from(this.pillsTarget.querySelectorAll(`input[name="${CSS.escape(inputName)}"]`))
      .map((el) => (el.value || "").trim())
      .filter((v) => v.length > 0)
      .map((v) => (this.uppercaseValue ? v.toUpperCase() : v))
  }

  formatLabel(value, selectedNameText) {
    if (this.useSelectedNameValue && selectedNameText.length > 0) {
      // selectedNameText typically looks like: "2081S0010X — Physical Medicine & Rehabilitation"
      return selectedNameText.replace(/\s+—\s+/g, " - ")
    }

    if (this.labelPrefixValue && this.labelPrefixValue.length > 0) {
      return `${this.labelPrefixValue} ${value}`
    }

    return value
  }

  clearAutocompleteSelection(refocus = false) {
    this.selectedIdFieldTarget.value = ""
    this.selectedNameFieldTarget.value = ""
    this.selectedIdFieldTarget.dispatchEvent(new Event("input"))
    this.selectedNameFieldTarget.dispatchEvent(new Event("input"))

    if (this.autocompleteInputTarget) {
      this.autocompleteInputTarget.value = ""
      if (refocus) this.autocompleteInputTarget.focus()
    }
  }
}


